import { useMediaQuery, useTheme } from "@mui/material";
import { CellContext, ColumnDef, SortingState } from "@tanstack/react-table";
import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Unauthorized } from "../../components";
import { DataTable } from "../../components/data-table/DataTable";
import { PageLayout } from "../../components/PageLayout";
import { useCheckAreaAccess } from "../../hooks/useCheckAreaAccess";
import { useLocalization } from "../../hooks/useLocalization";
import { Routes } from "../../types/NavigationPaths";
import { GrantLicenseDialog } from "./components/GrantLicenseDialog";
import { VerifyCompanyKeyDialog } from "./components/VerifyCompanyKeyDialog";
import { useCreateLicense } from "./hooks/useCreateLicense";
import { useExternalLicenses } from "./hooks/useExternalLicenses";
import { TCompanyLicenseRow } from "./types";
import { expirySortValue, formatDate, seatsSort } from "./utils";

const renderCompany = (ctx: CellContext<TCompanyLicenseRow, string>): string =>
  ctx.getValue<string>();

const renderTitle = (ctx: CellContext<TCompanyLicenseRow, string>): string =>
  ctx.getValue<string>();

const seatsAccessor = (row: TCompanyLicenseRow): string =>
  row.seats.total == null
    ? `${row.seats.used} / ∞`
    : `${row.seats.used} / ${row.seats.total}`;

const expiresAccessor = (row: TCompanyLicenseRow): string =>
  formatDate(row.expiresAt);

const expiresSort = (
  a: { original: TCompanyLicenseRow },
  b: { original: TCompanyLicenseRow },
): number =>
  expirySortValue(a.original.expiresAt) - expirySortValue(b.original.expiresAt);

const getCompanyLicenseColumns = (
  t: (key: string) => string,
): ColumnDef<TCompanyLicenseRow, any>[] => [
    {
      id: "companyName",
      header: t("licenses.licensesTableHeaders.company"),
      accessorKey: "companyName",
      cell: renderCompany,
      enableSorting: true,
      enableColumnFilter: true,
      sortingFn: "text",
      filterFn: "arrIncludesSome",
      size: 200,
      minSize: 200,
      maxSize: 303,
    },
    {
      id: "experienceName",
      header: t("licenses.licensesTableHeaders.title"),
      accessorKey: "experienceName",
      cell: renderTitle,
      enableSorting: true,
      enableColumnFilter: false,
      enableGlobalFilter: true,
      sortingFn: "text",
      size: 303,
      minSize: 200,
      maxSize: 303,
    },
    {
      id: "seats",
      header: t("licenses.licensesTableHeaders.seats"),
      accessorFn: seatsAccessor,
      sortingFn: seatsSort,
      enableSorting: true,
      enableColumnFilter: false,
      enableGlobalFilter: false,
      size: 96,
      minSize: 96,
      maxSize: 96,
    },
    {
      id: "expiresAt",
      header: t("licenses.licensesTableHeaders.expirationDate"),
      accessorFn: expiresAccessor,
      sortingFn: expiresSort,
      enableSorting: true,
      enableColumnFilter: false,
      enableGlobalFilter: false,
      size: 156,
      minSize: 156,
      maxSize: 156,
    },
  ];

const INITIAL_SORTING: SortingState = [{ id: "experienceName", desc: false }];

export const ExternalLicensesPage: React.FC = () => {
  const { t } = useLocalization();
  const navigate = useNavigate();
  const { companyId } = useParams();
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up("md"));

  const { isLoading: isCheckAccessLoading, access } = useCheckAreaAccess(companyId);
  const areHooksEnabled = !!companyId && !isCheckAccessLoading && access.license;
  const { licenses, loading } = useExternalLicenses(areHooksEnabled);


  const actionButtonText = mdUp
    ? t("licenses.grantLicense")
    : t("licenses.grant");

  const columns = useMemo(() => getCompanyLicenseColumns(t), [t]);

  const {
    isVerifyDialogOpen,
    isGrantDialogOpen,
    openVerifyDialog,
    handleCancelVerification,
    handleVerify,
    isVerifyError,
    isVerifying,
    companyKey,
    setCompanyKey,
    handleContinueFromVerifyDialog,
    verificationResults,
    closeGrantDialog,
    isSameCompany,
  } = useCreateLicense();

  const handleRowClick = React.useCallback(
    (row: TCompanyLicenseRow) => {
      void navigate(`/companies/${companyId}${Routes.EXTERNAL_LICENSES}/${row.id}`);
    },
    [navigate],
  );

  const hasAccessIds = useMemo(() => {
    if (!verificationResults?.[0]) return [];

    const companyId = verificationResults[0].companyId;

    const verifiedCompanyLicenses = licenses.filter(
      (lic) => lic.assignedOrganizationId === companyId,
    );

    return verifiedCompanyLicenses.map((lic) => lic.releaseChannelId);
  }, [licenses, verificationResults]);

  if (!loading && !access.license) {
    return (<Unauthorized />)
  }

  return (
    <PageLayout isLoading={loading} maxWidth={false}>
      <DataTable<TCompanyLicenseRow>
        actionButtonText={actionButtonText}
        columns={columns}
        data={licenses}
        defaultSort={INITIAL_SORTING}
        facetedColumnIds={["companyName"]}
        globalFilterPlaceholder={t("licenses.searchLicense")}
        onActionClick={openVerifyDialog}
        onRowClick={handleRowClick}
      />
      <VerifyCompanyKeyDialog
        companyKey={companyKey}
        handleCancel={handleCancelVerification}
        handleContinue={handleContinueFromVerifyDialog}
        handleVerify={handleVerify}
        isOpen={isVerifyDialogOpen}
        isSameCompany={isSameCompany}
        isSubmitLoading={isVerifying}
        isVerifyError={isVerifyError}
        setCompanyKey={setCompanyKey}
        verificationResults={verificationResults}
      />
      {verificationResults?.[0] && (
        <GrantLicenseDialog
          assignedOrganization={verificationResults[0]}
          assignedOrganizationKey={companyKey}
          existingLicensesReleaseChannelIds={hasAccessIds}
          isOpen={isGrantDialogOpen}
          onClose={closeGrantDialog}
        />
      )}
    </PageLayout>
  );
};
